import { z as t } from "zod";
const Ut = t.object({
  name: t.string(),
  id: t.string(),
  email: t.string(),
  role: t.string(),
  created_at: t.date(),
  deleted_at: t.date().nullable()
}), Nt = "editorTextBox", Q = t.object({
  orderId: t.string(),
  orderProductId: t.string(),
  applicationType: t.string(),
  userId: t.string(),
  funeralHomeId: t.string(),
  timestamp: t.string()
}), Ft = t.discriminatedUnion("type", [
  t.object({
    type: t.literal("EDITOR_BACK_BUTTON"),
    payload: Q
  })
]), Mt = t.object({
  orderId: t.string(),
  orderProductId: t.string(),
  applicationType: t.string()
}), xt = t.object({
  success: t.boolean(),
  message: t.string(),
  action: t.enum(["created", "existing", "processing", "skipped", "notified"]),
  pdfId: t.string().optional()
}), zt = {
  MDL_EDITOR_EVENTS: "mdl_editor_events"
}, Bt = t.object({
  fromDate: t.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: "Invalid date format. Expected format: yyyy-mm-dd"
  }).optional()
}), Gt = t.object({
  pageSetId: t.string().uuid(),
  themeId: t.string().uuid(),
  productId: t.string().uuid(),
  applyToTheme: t.string().uuid().optional(),
  funeralHomeId: t.string().uuid().optional()
}), Ht = t.object({
  sourceProductId: t.string().uuid(),
  targetProductId: t.string().uuid(),
  sourceThemeId: t.string().uuid().optional(),
  sourcePageSetIds: t.array(t.string().uuid()).optional(),
  funeralHomeId: t.string().uuid().optional()
}).refine((e) => e.sourceProductId !== e.targetProductId, {
  message: "Cannot copy to the same product",
  path: ["targetProductId"]
}), wt = t.object({
  productName: t.string().min(1),
  foldLine: t.enum(["vertical-center", "horizontal-center", "none"]),
  numberOfPages: t.number().min(1),
  thumbnailURL: t.string().url(),
  widthInches: t.number().min(1),
  heightInches: t.number().min(1),
  widthPixels: t.number().min(1),
  heightPixels: t.number().min(1),
  padding: t.object({
    top: t.number().min(0),
    right: t.number().min(0),
    bottom: t.number().min(0),
    left: t.number().min(0),
    centerHorizontal: t.number().min(0),
    centerVertical: t.number().min(0)
  })
}), kt = t.object({
  productId: t.string().uuid(),
  application: t.enum(["MDL", "M3"]),
  themeId: t.string().uuid().optional(),
  numberOfPages: t.number().int().min(1).optional()
}), Vt = t.object({
  baseThemeId: t.string().uuid(),
  newThemeName: t.string().min(1)
}), _ = 16383, $ = {
  prefix: "prefix",
  first: "first",
  nickname: "nickname",
  middle: "middle",
  last: "last",
  suffix: "suffix",
  maiden: "maiden"
}, L = ["M3", "MDL"], W = {
  // The AI image edits switch (Site Settings > AI Image Edits in M3) is off,
  // so no background removal or Enhance may start or be accepted.
  AI_IMAGE_EDITS_DISABLED: "AI_IMAGE_EDITS_DISABLED"
}, K = {
  QUEUED: "QUEUED",
  PROCESSING: "PROCESSING",
  COMPLETED: "COMPLETED",
  FAILED: "FAILED"
}, l = {
  BG_REMOVAL: "BG_REMOVAL",
  UPSCALE: "UPSCALE"
}, u = {
  DAILY_CAP_REACHED: "DAILY_CAP_REACHED",
  MODERATION_BLOCKED: "MODERATION_BLOCKED",
  SPEND_LIMIT: "SPEND_LIMIT",
  NOT_CONFIGURED: "NOT_CONFIGURED",
  MODEL_ERROR: "MODEL_ERROR",
  ENQUEUE_FAILED: "ENQUEUE_FAILED",
  ASPECT_UNSUPPORTED: "ASPECT_UNSUPPORTED",
  AI_IMAGE_EDITS_DISABLED: W.AI_IMAGE_EDITS_DISABLED
};
new Set(
  Object.values(u)
);
const X = {
  EDITING: "EDITING",
  PROOF_SENT: "PROOF_SENT",
  FINALIZED: "FINALIZED",
  CLOSED: "CLOSED"
}, o = {
  EN: "EN",
  ES: "ES",
  FR: "FR"
};
Object.values(o);
const C = {
  "Standard Theme Design": {
    prompt: "imagePlacement",
    steps: ({ imagePlacementChoice: e }) => e === "self" ? ["placement", "review"] : ["review"]
  },
  // The designer invents the background, so the theme selector is replaced by
  // the custom-theme brief; Continue goes straight to the review modal.
  "Custom Theme Design": {
    prompt: "customTheme",
    steps: () => ["review"]
  },
  // The order-of-service content rides in as a document upload or typed
  // text below the case dropdown; Continue goes straight to review.
  "Order of Service Partner": {
    prompt: "orderOfService",
    steps: () => ["review"]
  },
  // The requested layout adjustments ride in as free text below the case
  // dropdown; Continue goes straight to review.
  "Layout Partner": {
    prompt: "layoutChanges",
    steps: () => ["review"]
  },
  // Photo selections + edit comments are normally collected on the product
  // page; the modal step only runs when the customer hits Continue without
  // finishing them.
  "Photograph Editor": {
    prompt: "photoEdits",
    steps: ({ photoEditsComplete: e }) => e ? ["review"] : ["photoEdits", "review"]
  },
  // The extra photos (uploaded through the case-image pipeline or picked
  // from existing case images) are normally collected on the product page;
  // the modal step only runs when Continue is hit without any added.
  "Additional Photos": {
    prompt: "additionalPhotos",
    steps: ({ additionalPhotosComplete: e }) => e ? ["review"] : ["additionalPhotos", "review"]
  }
}, Y = "Signature by Beryl", Z = {
  [Y]: "Standard Theme Design",
  "Design my product using a standard theme": "Standard Theme Design",
  "Design my product using a custom theme": "Custom Theme Design",
  "Add an order of service": "Order of Service Partner",
  "Design my layouts": "Layout Partner",
  "Edit my photos": "Photograph Editor",
  "Add more photos": "Additional Photos"
};
new Map([
  ...Object.entries(C).map(
    ([e, n]) => [e.toLowerCase(), n]
  ),
  ...Object.entries(Z).map(
    ([e, n]) => [e.toLowerCase(), C[n]]
  )
]);
const J = [
  "unassigned",
  "assigned",
  "approved",
  "completed",
  "canceled"
], tt = (e) => e.trim().replace(/^['"]|['"]$/g, ""), et = (e) => tt(e.split(",")[0] ?? ""), nt = /^[A-Za-z0-9_-]*$/, a = t.enum(L).optional().default("MDL"), qt = t.object({
  categoryId: t.string().uuid().optional(),
  getAllAssets: t.boolean().optional()
}), Qt = t.object({
  categoryId: t.string().uuid().trim(),
  name: t.string().trim()
}), $t = t.object({
  categoryId: t.string().uuid().trim().optional(),
  name: t.string().trim().optional()
}), ot = t.object({
  subcategoryId: t.string().uuid().optional(),
  name: t.string().trim().optional()
}), Wt = t.object({
  subcategoryId: t.string().uuid(),
  name: t.string().trim(),
  image: t.string().trim()
}), Kt = ot.extend({
  image: t.string().trim().optional()
}), Xt = t.object({
  productId: t.string().uuid().optional(),
  themeId: t.string().uuid().optional()
}), Yt = t.object({
  productId: t.string().uuid(),
  themeId: t.string().uuid(),
  fontFamily: t.string(),
  textColor: t.string()
}), Zt = t.object({
  productId: t.string().uuid().optional().nullable(),
  themeId: t.string().uuid().optional().nullable(),
  fontFamily: t.string().optional().nullable(),
  textColor: t.string().optional().nullable()
}), it = 25, R = Object.values($), D = t.enum(R), A = (e) => new Set(e).size === e.length, U = t.object({
  order: t.array(D).length(R.length).refine(A, { message: "order must hold every part once" }),
  hidden: t.array(D).refine(A, { message: "hidden must list a part once" })
}).strict(), g = (e) => Array.isArray(e) ? e.filter(
  (n) => n && typeof n == "object" && "name" in n && typeof n.name == "string" && n.name.trim() !== ""
) : e, Jt = t.object({
  limit: t.coerce.number().int().min(1).max(50).optional().default(it),
  page: t.coerce.number().int().min(1).optional().default(1),
  archived: t.coerce.boolean().default(!1),
  searchTerm: t.string().optional(),
  recentOnly: t.coerce.boolean().default(!0)
}), b = t.array(
  t.object({
    id: t.string().optional().nullable(),
    location: t.string().trim().nullable().optional(),
    time: t.string().trim().nullable().optional(),
    date: t.string().nullable().optional(),
    type: t.string().trim().nonempty(),
    default: t.boolean().default(!1),
    city: t.string().trim().nullable().optional(),
    state: t.string().trim().nullable().optional(),
    address: t.string().trim().nullable().optional(),
    clergyPlace: t.string().trim().nullable().optional(),
    country: t.string().trim().nullable().optional(),
    endTime: t.string().trim().nullable().optional(),
    postalCode: t.string().trim().nullable().optional(),
    roomChapel: t.string().trim().nullable().optional(),
    startTime: t.string().trim().nullable().optional()
  })
).nullable().optional(), at = (e) => Array.isArray(e) ? e.filter(
  (n) => !(n !== null && typeof n == "object" && "name" in n && typeof n.name == "string" && n.name.trim() === "")
) : e, h = t.union([
  t.preprocess(
    at,
    t.array(
      t.object({
        name: t.string().trim().min(1, { message: "Name cannot be empty or just spaces" }),
        relation: t.string().trim().nullable().optional(),
        type: t.string().trim().min(1, { message: "Type cannot be empty or just spaces" }),
        city: t.string().trim().nullable().optional(),
        country: t.string().trim().nullable().optional(),
        state: t.string().trim().nullable().optional(),
        id: t.string().optional().nullable()
      })
    ).optional()
  ),
  t.string().trim().optional()
]).nullable().optional(), I = t.preprocess(
  g,
  t.array(
    t.object({
      name: t.string().trim().nonempty(),
      sequence: t.union([t.string().trim().nonempty(), t.number()]),
      charityOfChoice: t.boolean(),
      id: t.string().optional().nullable()
    })
  ).nullable().optional()
), f = t.preprocess(
  g,
  t.array(
    t.object({
      name: t.string().trim().nonempty(),
      sequence: t.union([t.string().trim(), t.number()]).nullable().optional(),
      type: t.string().trim().nonempty(),
      id: t.string().optional().nullable()
    })
  ).nullable().optional()
), S = t.preprocess(
  g,
  t.array(
    t.object({
      name: t.string().trim().nonempty(),
      sequence: t.union([t.string().trim().nonempty(), t.number()]),
      city: t.string().trim().nullable().optional(),
      state: t.string().trim().nullable().optional(),
      zipCode: t.string().trim().nullable().optional(),
      country: t.string().trim().nullable().optional(),
      websiteUrl: t.string().trim().nullable().optional(),
      address1: t.string().trim().nullable().optional(),
      address2: t.string().trim().nullable().optional(),
      id: t.string().optional().nullable()
    })
  ).nullable().optional()
), te = t.object({
  birth_header: t.string().optional(),
  death_header: t.string().optional(),
  remembrance_header: t.string().optional(),
  officiating_header: t.string().optional(),
  memorial_service_header: t.string().optional(),
  final_resting_header: t.string().optional(),
  pallbearers_header: t.string().optional(),
  visitation_header: t.string().optional(),
  family_appreciation_header: t.string().optional()
}), y = t.object({
  birthHeader: t.string().trim().optional(),
  deathHeader: t.string().trim().optional(),
  remembranceHeader: t.string().trim().optional(),
  officiatingHeader: t.string().trim().optional(),
  memorialServiceHeader: t.string().trim().optional(),
  finalRestingHeader: t.string().trim().optional(),
  pallbearersHeader: t.string().trim().optional(),
  visitationHeader: t.string().trim().optional(),
  familyAppreciationHeader: t.string().trim().optional()
}).nullable().optional(), rt = 2048, lt = /^https?:\/\//i, st = /^[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?([/?#]|$)/i, ut = (e) => {
  if (e === "") return !0;
  const n = lt.test(e) ? e : st.test(e) ? `https://${e}` : null;
  if (!n) return !1;
  try {
    const i = new URL(n);
    return (i.protocol === "http:" || i.protocol === "https:") && !!i.hostname;
  } catch {
    return !1;
  }
}, N = t.string().max(rt).refine(ut, {
  message: "returnUrl must be an http(s) URL or a host name without a scheme"
}).nullable().optional(), mt = t.object({
  nameOfDeceased: t.coerce.string().trim().nullable().optional(),
  nameArrangement: U.optional(),
  editableCaseId: t.string().trim().nullable().optional(),
  dateOfBirth: t.string().nullable().optional(),
  placeOfBirth: t.string().trim().nullable().optional(),
  dateOfDeath: t.string().nullable().optional(),
  placeOfDeath: t.string().trim().nullable().optional(),
  motherOfDeceased: t.string().trim().nullable().optional(),
  fatherOfDeceased: t.string().trim().nullable().optional(),
  spouse: t.string().trim().nullable().optional(),
  dateOfMarriage: t.coerce.date().nullable().optional(),
  maternalGrandmother: t.string().trim().nullable().optional(),
  maternalGrandmothersMother: t.string().trim().nullable().optional(),
  maternalGrandmothersFather: t.string().trim().nullable().optional(),
  maternalGrandfather: t.string().trim().nullable().optional(),
  maternalGrandfathersMother: t.string().trim().nullable().optional(),
  maternalGrandfathersFather: t.string().trim().nullable().optional(),
  paternalGrandmother: t.string().trim().nullable().optional(),
  paternalGrandmothersMother: t.string().trim().nullable().optional(),
  paternalGrandmothersFather: t.string().trim().nullable().optional(),
  paternalGrandfather: t.string().trim().nullable().optional(),
  paternalGrandfathersMother: t.string().trim().nullable().optional(),
  paternalGrandfathersFather: t.string().trim().nullable().optional(),
  children: t.array(t.string()).default([]),
  siblings: t.array(t.string()).default([]),
  organizations: S,
  songs: t.array(t.string()).default([]),
  pallbearers: f,
  obituary: t.string().trim().nullable().optional(),
  familyAppreciationMessage: t.string().trim().nullable().optional(),
  services: b,
  additionalFamily: h,
  clergy: t.array(t.string()).default([]),
  intermentPlace: t.string().trim().nullable().optional(),
  intermentLot: t.string().trim().nullable().optional(),
  intermentSection: t.string().trim().nullable().optional(),
  intermentBlock: t.string().trim().nullable().optional(),
  intermentCity: t.string().trim().nullable().optional(),
  intermentState: t.string().trim().nullable().optional(),
  intermentDate: t.string().nullable().optional(),
  intermentCounty: t.string().trim().nullable().optional(),
  intermentTime: t.string().trim().nullable().optional(),
  createdOnBehalfOfEmail: t.string().trim().optional(),
  intermentAddress: t.string().trim().optional(),
  lastdec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  firstDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  maidenDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  middleDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  nickNameDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  prefixDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  suffixDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  websiteObitUrl: t.string().trim().optional(),
  orderOfService: t.string().trim().optional(),
  memorialContributions: I,
  returnUrl: N,
  intermentPostalCode: t.string().trim().optional(),
  casePreferences: y
}), ee = t.object({
  nameOfDeceased: t.coerce.string().trim().nullable().optional(),
  nameArrangement: U.optional(),
  editableCaseId: t.string().trim().nullable().optional(),
  dateOfBirth: t.string().nullable().optional(),
  placeOfBirth: t.string().trim().nullable().optional(),
  dateOfDeath: t.string().nullable().optional(),
  placeOfDeath: t.string().trim().nullable().optional(),
  motherOfDeceased: t.string().trim().nullable().optional(),
  fatherOfDeceased: t.string().trim().nullable().optional(),
  spouse: t.string().trim().nullable().optional(),
  dateOfMarriage: t.coerce.date().nullable().optional(),
  maternalGrandmother: t.string().trim().nullable().optional(),
  maternalGrandmothersMother: t.string().trim().nullable().optional(),
  maternalGrandmothersFather: t.string().trim().nullable().optional(),
  maternalGrandfather: t.string().trim().nullable().optional(),
  maternalGrandfathersMother: t.string().trim().nullable().optional(),
  maternalGrandfathersFather: t.string().trim().nullable().optional(),
  paternalGrandmother: t.string().trim().nullable().optional(),
  paternalGrandmothersMother: t.string().trim().nullable().optional(),
  paternalGrandmothersFather: t.string().trim().nullable().optional(),
  paternalGrandfather: t.string().trim().nullable().optional(),
  paternalGrandfathersMother: t.string().trim().nullable().optional(),
  paternalGrandfathersFather: t.string().trim().nullable().optional(),
  children: t.array(t.string()).optional(),
  siblings: t.array(t.string()).optional(),
  organizations: S,
  songs: t.array(t.string()).optional(),
  pallbearers: f,
  obituary: t.string().trim().nullable().optional(),
  familyAppreciationMessage: t.string().trim().nullable().optional(),
  services: b,
  additionalFamily: h,
  clergy: t.array(t.string()).optional(),
  intermentPlace: t.string().trim().nullable().optional(),
  intermentLot: t.string().trim().nullable().optional(),
  intermentSection: t.string().trim().nullable().optional(),
  intermentBlock: t.string().trim().nullable().optional(),
  intermentCity: t.string().trim().nullable().optional(),
  intermentState: t.string().trim().nullable().optional(),
  intermentDate: t.string().nullable().optional(),
  intermentCounty: t.string().trim().nullable().optional(),
  intermentTime: t.string().trim().nullable().optional(),
  createdOnBehalfOfEmail: t.string().trim().optional(),
  intermentAddress: t.string().trim().optional(),
  lastdec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  firstDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  maidenDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  middleDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  nickNameDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  prefixDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  suffixDec: t.union([t.string(), t.boolean()]).transform((e) => typeof e == "boolean" ? e ? "True" : "False" : e.trim()).nullable().optional(),
  websiteObitUrl: t.string().trim().optional(),
  orderOfService: t.string().trim().optional(),
  memorialContributions: I,
  returnUrl: N,
  intermentPostalCode: t.string().trim().optional(),
  casePreferences: y
}), ne = t.object({
  services: b,
  additionalFamily: h,
  memorialContributions: I,
  pallbearers: f,
  organizations: S,
  casePreferences: y
}), oe = t.object({
  caseId: t.string().uuid(),
  contentType: t.string().regex(/^image\//, "contentType must be an image MIME type")
}), ie = t.object({
  caseId: t.string().uuid(),
  s3Key: t.string().min(1),
  originalFileName: t.string().optional()
}), ae = t.object({
  caseId: t.string().uuid(),
  imageUrl: t.string().url()
}), re = t.object({
  caseId: t.string().uuid(),
  fromIndex: t.number().int().nonnegative()
}), le = t.object({
  caseId: t.string().uuid(),
  contentTypes: t.array(
    t.string().regex(/^image\//, "contentType must be an image MIME type")
  ).min(1).max(20)
}), se = t.object({
  caseId: t.string().uuid(),
  items: t.array(
    t.object({
      s3Key: t.string().min(1),
      originalFileName: t.string().optional()
    })
  ).min(1).max(20)
}), ue = t.object({
  jobIds: t.array(t.string().min(1)).min(1).max(20)
}), me = t.object({
  token: t.string().min(1),
  contentType: t.string().regex(/^image\//, "contentType must be an image MIME type")
}), ce = t.object({
  token: t.string().min(1),
  s3Key: t.string().min(1),
  originalFileName: t.string().optional()
}), pe = t.object({
  token: t.string().min(1),
  imageUrl: t.string().url()
}), de = t.object({
  token: t.string().min(1)
}), ge = t.object({
  token: t.string().min(1),
  imageUrl: t.string().url()
}), be = t.object({
  token: t.string().min(1)
}), s = `targetSize edges must be whole pixels from 1 to ${_}`, ct = "targetSize is only accepted with kind UPSCALE", v = t.number({ message: s }).int({ message: s }).min(1, { message: s }).max(_, {
  message: s
}), pt = t.object({
  width: v,
  height: v
}), he = t.object({
  caseImageId: t.string().uuid(),
  kind: t.nativeEnum(l),
  sourceVariantId: t.string().uuid().optional(),
  targetSize: pt.optional()
}).refine(
  (e) => e.targetSize === void 0 || e.kind === l.UPSCALE,
  { message: ct, path: ["targetSize"] }
), Ie = t.object({
  code: t.enum([
    u.DAILY_CAP_REACHED,
    u.ASPECT_UNSUPPORTED
  ])
}), F = t.object({
  id: t.string().uuid(),
  kind: t.nativeEnum(l),
  status: t.nativeEnum(K),
  url: t.string().nullable(),
  sourceVariantId: t.string().uuid().nullable(),
  accepted: t.boolean(),
  revertedAt: t.string().datetime().nullable(),
  errorCode: t.nativeEnum(u).nullable(),
  createdAt: t.string().datetime()
}), fe = t.object({
  variants: t.array(F)
}), dt = F.extend({
  caseImageId: t.string().uuid()
}), Se = t.object({
  variants: t.array(dt)
}), ye = t.object({
  reverted: t.boolean()
}), Ee = t.object({
  variantId: t.string().uuid(),
  revertedAt: t.string().datetime().nullable()
}), Te = t.object({
  [l.BG_REMOVAL]: t.number().int().nonnegative(),
  [l.UPSCALE]: t.number().int().nonnegative()
}), je = t.object({
  enabled: t.boolean()
}), Oe = t.object({
  caseId: t.string(),
  archived: t.coerce.boolean().default(!1)
}), Pe = t.object({
  memorial: t.string().trim(),
  submitterName: t.string().trim(),
  caseId: t.string()
}), Ce = t.object({
  htmlForRender: t.string().trim(),
  html: t.string().trim(),
  reference: t.string().trim(),
  isGlobal: t.boolean().optional().nullable(),
  imageUrl: t.string().optional().nullable(),
  location: t.string().optional().nullable(),
  type: t.enum(["HTML", "IMAGE"]).default("HTML"),
  themeId: t.string().uuid().optional().nullable(),
  baseProductId: t.string().uuid().optional().nullable(),
  orderProductId: t.string().uuid().optional().nullable()
}), De = t.object({
  themeId: t.string().uuid().optional().nullable(),
  baseProductId: t.string().uuid().optional().nullable(),
  orderProductId: t.string().uuid().optional().nullable()
}), M = t.enum(["button", "thumbnail"]), E = t.enum([
  "base",
  "enhancement",
  "theme",
  "template",
  "dynamic_content"
]), x = t.enum([
  "all",
  "specific",
  "all_plus_specific",
  "all_except"
]), gt = t.object({
  customizationLabel: t.string(),
  optionLabels: t.array(t.string())
}), m = t.object({
  label: t.string().trim().min(1),
  image: t.string().optional(),
  iconAssetUrl: t.string().url().optional(),
  sortOrder: t.number().int().optional(),
  available: t.boolean().optional(),
  description: t.string().optional(),
  themeId: t.string().uuid().optional(),
  dependsOn: t.array(gt).optional()
}), Ae = m.partial(), bt = t.object({
  productId: t.string().uuid(),
  label: t.string().trim().min(1),
  displayType: M.default("button"),
  customizationType: E.default("base"),
  setDefault: t.boolean().optional(),
  defaultOption: t.string().uuid().optional(),
  available: t.boolean().default(!0),
  sortOrder: t.number().int().min(0).optional(),
  themeMode: x.optional(),
  options: t.array(m).optional()
}).strict(), ve = bt.refine(
  (e) => !(e.setDefault === !0 && !e.defaultOption),
  {
    message: "defaultOption is required when setDefault is true",
    path: ["defaultOption"]
  }
).refine(
  (e) => {
    if (e.defaultOption)
      try {
        return t.string().uuid().parse(e.defaultOption), !0;
      } catch {
        return !1;
      }
    return !0;
  },
  {
    message: "defaultOption must be a valid UUID",
    path: ["defaultOption"]
  }
).refine(
  (e) => {
    if (e.options && e.options.length > 0) {
      const n = e.options.map((r) => r.label.toLowerCase()), i = new Set(n);
      return n.length === i.size;
    }
    return !0;
  },
  {
    message: "Option labels must be unique (case-insensitive)",
    path: ["options"]
  }
), ht = t.object({
  productId: t.string().uuid().optional(),
  label: t.string().trim().min(1).optional(),
  displayType: M.optional(),
  customizationType: E.optional(),
  setDefault: t.boolean().optional(),
  defaultOption: t.string().uuid().optional(),
  available: t.boolean().optional(),
  sortOrder: t.number().int().min(0).optional(),
  themeMode: x.nullable().optional(),
  options: t.array(m).optional(),
  archived: t.boolean().optional()
}).strict(), _e = ht.refine(
  (e) => !(e.setDefault === !0 && !e.defaultOption),
  {
    message: "defaultOption is required when setDefault is true",
    path: ["defaultOption"]
  }
).refine(
  (e) => {
    if (e.defaultOption)
      try {
        return t.string().uuid().parse(e.defaultOption), !0;
      } catch {
        return !1;
      }
    return !0;
  },
  {
    message: "defaultOption must be a valid UUID",
    path: ["defaultOption"]
  }
).refine(
  (e) => {
    if (e.options && e.options.length > 0) {
      const n = e.options.map((r) => r.label.toLowerCase()), i = new Set(n);
      return n.length === i.size;
    }
    return !0;
  },
  {
    message: "Option labels must be unique (case-insensitive)",
    path: ["options"]
  }
), Le = t.object({
  productId: t.string().uuid().optional(),
  archived: t.boolean().optional(),
  available: t.boolean().optional(),
  customizationType: E.optional()
}), Re = t.object({
  id: t.string().uuid()
}), Ue = t.object({
  id: t.string().uuid()
}), Ne = t.object({
  options: t.array(m).min(1)
}), T = t.object({
  customizationId: t.string().uuid(),
  selectedOptionId: t.string().uuid()
}), j = t.record(t.string(), t.string()), Fe = t.object({
  bucket: t.enum(J).optional()
}), Me = t.object({
  id: t.string().uuid()
}), xe = t.object({
  themeOverrideId: t.string(),
  archived: t.coerce.boolean().default(!1)
}), It = t.object({
  pageId: t.string().uuid(),
  content: t.string().min(1),
  loadedRunId: t.string().min(1).max(128).nullable(),
  boxes: t.array(t.unknown()),
  deleteBoxIds: t.array(t.string().min(1)).default([])
}).strict(), ze = t.object({
  applicationType: t.enum(L),
  coveringVersionNumber: t.number().int().positive(),
  runId: t.string().min(1).max(128),
  pages: t.array(It).min(1)
}).strict().superRefine((e, n) => {
  const i = /* @__PURE__ */ new Set();
  for (const [r, d] of e.pages.entries())
    i.has(d.pageId) && n.addIssue({
      code: t.ZodIssueCode.custom,
      path: ["pages", r, "pageId"],
      message: `page "${d.pageId}" appears twice — one entry per page`
    }), i.add(d.pageId);
}), ft = {
  maxRetries: 2,
  maxWaitMs: 1e4,
  timeoutMs: 12e4
}, Be = () => {
  const { maxRetries: e, maxWaitMs: n, timeoutMs: i } = ft;
  return e * (n + i);
}, z = t.enum(["button", "thumbnail"]), O = t.enum([
  "base",
  "enhancement",
  "theme",
  "template"
]), St = t.enum(["GOOD", "BETTER", "BEST"]), B = t.enum([
  "all",
  "specific",
  "all_plus_specific",
  "all_except"
]), P = t.object({
  label: t.string().trim().min(1),
  image: t.string().optional(),
  sortOrder: t.number().int().optional(),
  available: t.boolean().optional(),
  quality: St.optional(),
  description: t.string().optional(),
  themeId: t.string().uuid().optional(),
  dependsOn: t.array(
    t.object({
      customizationLabel: t.string(),
      optionLabels: t.array(t.string())
    })
  ).optional()
}), Ge = P.partial(), He = t.object({
  categoryId: t.string().uuid(),
  productId: t.string().uuid().optional(),
  label: t.string().trim().min(1),
  displayType: z,
  customizationType: O.default("base"),
  setDefault: t.boolean().optional(),
  defaultOption: t.string().uuid().optional(),
  available: t.boolean().default(!0),
  sortOrder: t.number().int().min(0).optional(),
  themeMode: B.optional(),
  options: t.array(P).optional()
}).strict().refine(
  (e) => !(e.setDefault === !0 && !e.defaultOption),
  {
    message: "defaultOption is required when setDefault is true",
    path: ["defaultOption"]
  }
).refine(
  (e) => {
    if (e.defaultOption)
      try {
        return t.string().uuid().parse(e.defaultOption), !0;
      } catch {
        return !1;
      }
    return !0;
  },
  {
    message: "defaultOption must be a valid UUID",
    path: ["defaultOption"]
  }
).refine(
  (e) => {
    if (e.options && e.options.length > 0) {
      const n = e.options.map((r) => r.label.toLowerCase()), i = new Set(n);
      return n.length === i.size;
    }
    return !0;
  },
  {
    message: "Option labels must be unique (case-insensitive)",
    path: ["options"]
  }
), we = t.object({
  categoryId: t.string().uuid().optional(),
  productId: t.string().uuid().optional(),
  label: t.string().trim().min(1).optional(),
  displayType: z.optional(),
  customizationType: O.optional(),
  setDefault: t.boolean().optional(),
  defaultOption: t.string().uuid().optional(),
  available: t.boolean().optional(),
  sortOrder: t.number().int().min(0).optional(),
  themeMode: B.nullable().optional(),
  options: t.array(P).optional(),
  archived: t.boolean().optional()
}).strict().refine(
  (e) => !(e.setDefault === !0 && !e.defaultOption),
  {
    message: "defaultOption is required when setDefault is true",
    path: ["defaultOption"]
  }
).refine(
  (e) => {
    if (e.defaultOption)
      try {
        return t.string().uuid().parse(e.defaultOption), !0;
      } catch {
        return !1;
      }
    return !0;
  },
  {
    message: "defaultOption must be a valid UUID",
    path: ["defaultOption"]
  }
).refine(
  (e) => {
    if (e.options && e.options.length > 0) {
      const n = e.options.map((r) => r.label.toLowerCase()), i = new Set(n);
      return n.length === i.size;
    }
    return !0;
  },
  {
    message: "Option labels must be unique (case-insensitive)",
    path: ["options"]
  }
), ke = t.object({
  categoryId: t.string().uuid().optional(),
  productId: t.string().uuid().optional(),
  archived: t.boolean().optional(),
  available: t.boolean().optional(),
  customizationType: O.optional()
}), Ve = t.object({
  id: t.string().uuid()
}), qe = t.object({
  id: t.string().uuid()
}), yt = t.enum([
  "paper_stock",
  "cover",
  "binding",
  "envelope"
]), Qe = t.object({
  type: yt
}), Et = t.enum(["PLAIN", "PREPRINTED"]), G = t.object({
  name: t.string().trim().min(1),
  itmRef: t.string().trim().min(1),
  priceCode: t.string().trim().min(1),
  priceTable: t.string().trim().min(1),
  description: t.string().trim().optional(),
  quantityIncrements: t.number().int().positive().optional(),
  minimumQuantity: t.number().int().positive().optional(),
  fontColor: t.string().optional(),
  themeId: t.string().uuid().optional(),
  paperWidth: t.number().positive().optional(),
  paperHeight: t.number().positive().optional(),
  // Editor-canvas pixel dimensions. Transient: used only to seed default
  // layout page content, never persisted — theme_width/theme_height columns
  // always mirror paper_width/paper_height (inches), same as MDL.
  widthPixels: t.number().positive().optional(),
  heightPixels: t.number().positive().optional(),
  foldLine: t.string().optional(),
  marginTop: t.number().int().min(0).optional(),
  marginRight: t.number().int().min(0).optional(),
  marginBottom: t.number().int().min(0).optional(),
  marginLeft: t.number().int().min(0).optional(),
  marginCenterHorizontal: t.number().int().min(0).optional(),
  marginCenterVertical: t.number().int().min(0).optional(),
  backgroundType: Et.optional(),
  identifier: t.record(t.string()).optional(),
  hasVariations: t.boolean().optional(),
  available: t.boolean().optional()
}), $e = G.partial().extend({
  archived: t.boolean().optional()
}), We = t.object({
  archived: t.boolean().optional(),
  available: t.boolean().optional(),
  priceCode: t.string().optional(),
  priceTable: t.string().optional(),
  searchTerm: t.string().optional()
}), Ke = t.object({
  id: t.string().uuid()
}), Xe = t.object({
  itmRef: t.string().min(1)
}), Ye = t.array(G), Tt = t.object({
  customizationId: t.string().uuid().optional(),
  selectedOptionId: t.string().uuid().optional(),
  themeId: t.string().uuid().optional()
}).refine(
  (e) => {
    const n = !!e.customizationId && !!e.selectedOptionId, i = !!e.themeId, r = (e.customizationId ? 1 : 0) + (e.selectedOptionId ? 1 : 0);
    return n && !i && r === 2 || i && r === 0;
  },
  {
    message: "Selection must be either { customizationId, selectedOptionId } or { themeId }"
  }
), jt = t.object({
  baseProductId: t.string().uuid(),
  name: t.string().trim().min(1),
  itmRef: t.string().trim().min(1),
  priceCode: t.string().trim().min(1),
  priceTable: t.string().trim().min(1),
  description: t.string().trim().optional(),
  selections: t.array(Tt).min(1)
}), Ze = jt.partial().extend({
  archived: t.boolean().optional()
}), Je = t.object({
  id: t.string().uuid()
}), H = t.object({
  productId: t.string().uuid().optional(),
  productVariationId: t.string().uuid().optional(),
  categoryId: t.string().uuid().optional(),
  themeId: t.string().uuid(),
  imageUrl: t.string().url(),
  sortOrder: t.number().int().min(0).optional()
}), tn = H.partial().omit({
  productId: !0,
  productVariationId: !0,
  categoryId: !0
}), en = t.object({
  id: t.string().uuid()
}), nn = t.object({
  productId: t.string().uuid().optional(),
  productVariationId: t.string().uuid().optional(),
  categoryId: t.string().uuid().optional(),
  themeId: t.string().uuid().optional()
}), on = t.array(
  H
), w = t.object({
  productId: t.string().uuid(),
  quantity: t.number().int().positive().optional(),
  sortOrder: t.number().int().min(0).optional()
}), an = t.object({
  funeralHomeId: t.string().uuid(),
  name: t.string().trim().min(1, "Name is required"),
  description: t.string().trim().optional(),
  products: t.array(w).optional()
}), rn = t.object({
  name: t.string().trim().min(1, "Name is required").optional(),
  description: t.string().trim().optional(),
  archived: t.boolean().optional(),
  products: t.array(w).optional()
}), ln = t.object({
  funeralHomeId: t.string().uuid().optional(),
  archived: t.boolean().optional(),
  searchTerm: t.string().optional()
}), sn = t.object({
  id: t.string().uuid()
}), un = t.object({
  bundleId: t.string().uuid(),
  productId: t.string().uuid(),
  quantity: t.number().int().positive().optional(),
  sortOrder: t.number().int().min(0).optional()
}), mn = t.object({
  bundleId: t.string().uuid(),
  productId: t.string().uuid()
}), cn = t.object({
  bundleId: t.string().uuid(),
  productId: t.string().uuid(),
  quantity: t.number().int().positive()
}), Ot = t.object({
  name: t.string().trim().min(1, "Name is required"),
  description: t.string().trim().optional(),
  image: t.string().trim().optional(),
  short_code: t.string().trim().optional(),
  available: t.boolean().optional()
}), pn = t.object({
  name: t.string().trim().min(1, "Name is required").optional(),
  description: t.string().trim().optional(),
  image: t.string().trim().optional().nullable(),
  short_code: t.string().trim().optional(),
  available: t.boolean().optional()
}), dn = t.object({
  available: t.boolean().optional(),
  searchTerm: t.string().optional()
}), gn = t.object({
  id: t.string().uuid()
}), bn = t.object({
  id: t.string().uuid(),
  available: t.boolean()
}), hn = t.array(
  Ot
), In = t.object({
  applicationType: a,
  orderPageSetId: t.string().uuid()
}), fn = t.object({
  archived: t.boolean().default(!1)
}), Sn = t.object({
  pageSetId: t.string().optional(),
  orderPageSetId: t.string().optional(),
  homeTemplate: t.string().optional(),
  default: t.boolean().optional(),
  json_template: t.string().optional(),
  archived: t.boolean().default(!1)
}), yn = t.object({
  applicationType: a,
  orderPageSetId: t.string().uuid()
}), En = t.object({
  id: t.string().uuid()
}), Tn = t.object({
  caseId: t.string().uuid().optional(),
  themeId: t.string().uuid().nullable().optional(),
  archived: t.coerce.boolean().optional().default(!1),
  language: t.nativeEnum(o).optional()
}), jn = t.object({
  caseId: t.string().uuid().optional(),
  themeId: t.string().uuid().nullable().optional(),
  archived: t.coerce.boolean().optional().default(!1),
  language: t.nativeEnum(o).optional(),
  m3OrderProductId: t.string().uuid().optional()
}), On = t.object({
  applicationType: a.optional(),
  orderId: t.string().uuid().optional(),
  caseId: t.string().uuid().optional()
}).refine((e) => !!(e.orderId || e.caseId), {
  message: "Either orderId or caseId is required",
  path: ["orderId"]
}), Pn = t.object({
  orderId: t.string().uuid().optional()
}), Cn = t.object({
  applicationType: a,
  name: t.string().trim().optional(),
  selected: t.coerce.boolean().optional(),
  archived: t.coerce.boolean().optional(),
  status: t.nativeEnum(X).optional(),
  themeId: t.string().uuid().nullable().optional()
}), Dn = t.object({
  applicationType: t.enum(["M3", "MDL"]).optional(),
  funeralHomeId: t.string().uuid().optional(),
  name: t.string().trim().optional(),
  productId: t.string().uuid().optional(),
  m3ProductId: t.string().uuid().optional(),
  orderProductId: t.string().uuid().optional(),
  id: t.string().uuid().optional(),
  language: t.nativeEnum(o).optional()
}), An = t.object({
  applicationType: a,
  gridStartPointTop: t.number().optional(),
  gridStartPointLeft: t.number().optional(),
  pageNumber: t.number().optional(),
  columns: t.number().optional(),
  rows: t.number().optional(),
  content: t.string().optional(),
  jsonTemplate: t.string().optional(),
  /**
   * Why the client is writing this page, recorded as the reason of the
   * version snapshot the write takes. Absent means a person saved the page
   * (`page_save`); `watermark_sync` is the customize page writing the theme's
   * watermarks onto page 1 with nobody editing. The values mirror
   * SNAPSHOT_REASONS in @messenger/utils.
   */
  saveReason: t.enum(["page_save", "watermark_sync"]).optional(),
  /**
   * The version the client already took for the Save this write belongs to.
   * When that version holds this page as it stands, the write takes no
   * version of its own and the whole Save is covered by one. Absent — from an
   * older client, or a write that is not part of a Save — leaves every write
   * taking its own.
   */
  coveringVersionNumber: t.number().int().positive().optional()
}), c = t.string().trim().max(50).nullable().optional().transform((e) => e === void 0 ? void 0 : e || null), vn = t.object({
  name: t.string().trim(),
  productId: t.string().uuid().optional(),
  m3ProductId: t.string().uuid().optional(),
  orderId: t.string().uuid(),
  selected: t.coerce.boolean(),
  themeId: t.string().uuid().optional(),
  // Per-line language override. null/omitted = inherit the order language.
  language: t.nativeEnum(o).nullable().optional(),
  nickname: c
}), _n = t.object({
  caseId: t.string().uuid(),
  themeId: t.string().uuid().optional(),
  language: t.nativeEnum(o).optional().default(o.EN)
}), Ln = t.object({
  caseId: t.string().uuid().optional(),
  themeId: t.string().uuid().nullable().optional(),
  status: t.string().optional(),
  language: t.nativeEnum(o).optional()
}), Rn = t.object({
  name: t.string().trim().optional(),
  available: t.coerce.boolean().optional(),
  default: t.coerce.boolean().optional(),
  productId: t.string().uuid().optional(),
  m3ProductId: t.string().uuid().optional(),
  orderProductDetailsId: t.string().uuid().optional()
}), Un = t.object({
  name: t.string().trim().optional(),
  available: t.coerce.boolean().optional(),
  default: t.coerce.boolean().optional(),
  productId: t.string().uuid().optional(),
  m3ProductId: t.string().uuid().optional(),
  orderProductDetailsId: t.string().uuid().optional()
}), Nn = t.object({
  applicationType: a,
  orderPageSetId: t.string().uuid(),
  gridStartPointTop: t.number(),
  gridStartPointLeft: t.number(),
  rows: t.number(),
  columns: t.number(),
  pageNumber: t.number(),
  jsonTemplate: t.string(),
  sourcePageId: t.string().uuid().optional()
}), Fn = t.object({
  applicationType: a,
  templateId: t.string().uuid(),
  templateType: t.enum(["GLOBAL", "FUNERAL_HOME"]),
  pages: t.array(
    t.object({
      gridStartPointTop: t.number(),
      gridStartPointLeft: t.number(),
      rows: t.number(),
      columns: t.number(),
      pageNumber: t.number(),
      jsonTemplate: t.string(),
      sourcePageId: t.string().uuid()
    })
  ).min(1)
}), Mn = t.object({
  applicationType: a,
  name: t.string().trim(),
  productId: t.string().uuid().optional(),
  m3ProductId: t.string().uuid().optional(),
  orderProductId: t.string().uuid(),
  pageSetId: t.string().uuid(),
  homeTemplate: t.boolean().optional(),
  default: t.boolean().optional(),
  language: t.nativeEnum(o).optional().default(o.EN)
}), xn = t.object({
  applicationType: a,
  orderId: t.string().uuid()
}), zn = t.object({
  applicationType: a,
  name: t.string().trim().optional(),
  productId: t.string().uuid().optional(),
  m3ProductId: t.string().uuid().optional(),
  pageSetId: t.string().uuid().optional(),
  funeralHomeTemplateId: t.string().uuid().optional(),
  globalTemplateId: t.string().uuid().optional(),
  default: t.boolean().optional(),
  language: t.nativeEnum(o).optional(),
  type: t.enum(["GLOBAL", "FUNERAL_HOME"]).optional(),
  currentOrderPageSetIdUsed: t.string().uuid().optional()
}), Bn = t.object({
  applicationType: a
}), Gn = t.object({
  ids: t.array(t.string().uuid()).min(1),
  applicationType: t.enum(["M3", "MDL"]).optional()
}), Hn = t.object({
  caseId: t.string().uuid(),
  orderId: t.string().uuid()
}), wn = t.object({
  orderId: t.string().uuid(),
  language: t.nativeEnum(o)
}), Pt = t.object({
  id: t.string().trim().uuid().optional(),
  productId: t.string().trim(),
  themeId: t.string().trim().optional(),
  // Per-line language. Language is materialized on every line (theme parity):
  // omitted on create = the order language is written onto the line; `null` on
  // update = reset the line back to the order language. Omit on update to
  // leave the stored value unchanged.
  language: t.nativeEnum(o).nullable().optional(),
  // Per-line label shown as "Name (nickname)". `null` (or "") clears it;
  // omit to leave the stored value unchanged.
  nickname: c
}), k = Pt.extend({
  customizations: t.array(T).optional(),
  customizationsByLabel: j.optional()
}), V = t.object({
  name: t.string().min(1),
  createdBy: t.string().optional().nullable(),
  canInsertImages: t.boolean().optional().nullable(),
  archived: t.boolean().optional().nullable()
}).optional().nullable(), kn = t.object({
  funeralHome: t.object({
    id: t.string().trim().optional(),
    name: t.string().trim(),
    phone: t.string().nullable().optional(),
    address: t.string().nullable().optional(),
    city: t.string().nullable().optional(),
    state: t.string().nullable().optional(),
    zipCode: t.string().nullable().optional(),
    corporationId: t.string().nullable().optional(),
    active: t.boolean().default(!0),
    ipCity: t.string().nullable().optional(),
    ipCountryCode: t.string().nullable().optional(),
    ipCountryName: t.string().nullable().optional(),
    ipAddress: t.string().nullable().optional(),
    ipLatitude: t.number().nullable().optional(),
    ipLongitude: t.number().nullable().optional(),
    ipRegionCode: t.string().nullable().optional(),
    ipRegionName: t.string().nullable().optional(),
    ipZipCode: t.string().nullable().optional(),
    isServiceAuthenticated: t.boolean().default(!1),
    freeTrialStartedAt: t.date().nullable().optional(),
    serviceUrlSlug: t.string().nullable().optional(),
    stripeCustomerId: t.string().nullable().optional(),
    stripeSubscriptionStatus: t.string().nullable().optional(),
    stripeSubscriptionEndDate: t.date().nullable().optional(),
    address2: t.string().nullable().optional(),
    emailAddress: t.string().nullable().optional(),
    websiteUrl: t.string().nullable().optional()
  }),
  funeralHomeSettings: t.object({
    datesOfLifeFormat: t.string().max(150).optional(),
    datesOfServiceFormat: t.string().max(105).optional(),
    birthHeader: t.string().max(500).optional(),
    deathHeader: t.string().max(500).optional(),
    remembranceHeader: t.string().max(500).optional(),
    language: t.nativeEnum(o),
    timePeriod: t.string().max(50).optional(),
    timeRangeSeparator: t.string().max(10).optional(),
    showTimeNoon: t.boolean().optional(),
    removeMinutes: t.boolean().optional()
  }).optional(),
  user: t.object({
    id: t.string().trim().optional(),
    name: t.string().trim(),
    email: t.string().email(),
    corporationId: t.string().nullable().optional()
  }),
  case: mt,
  // Optional so the middleware can create an empty order and add each line
  // afterwards via POST /api/order/:orderId/product; omitting the key is the
  // same as sending an empty array.
  products: t.union([
    t.string().array(),
    k.array()
  ]).optional().default([]),
  themeId: t.string().trim(),
  corporation: V,
  language: t.nativeEnum(o).optional().default(o.EN)
}), Vn = t.object({
  funeralHome: t.object({
    id: t.string().trim().optional(),
    name: t.string().trim(),
    phone: t.string().nullable().optional(),
    address: t.string().nullable().optional(),
    city: t.string().nullable().optional(),
    state: t.string().nullable().optional(),
    zipCode: t.string().nullable().optional(),
    corporationId: t.string().nullable().optional(),
    active: t.boolean().default(!0),
    ipCity: t.string().nullable().optional(),
    ipCountryCode: t.string().nullable().optional(),
    ipCountryName: t.string().nullable().optional(),
    ipAddress: t.string().nullable().optional(),
    ipLatitude: t.number().nullable().optional(),
    ipLongitude: t.number().nullable().optional(),
    ipRegionCode: t.string().nullable().optional(),
    ipRegionName: t.string().nullable().optional(),
    ipZipCode: t.string().nullable().optional(),
    isServiceAuthenticated: t.boolean().default(!1),
    freeTrialStartedAt: t.date().nullable().optional(),
    serviceUrlSlug: t.string().nullable().optional(),
    stripeCustomerId: t.string().nullable().optional(),
    stripeSubscriptionStatus: t.string().nullable().optional(),
    stripeSubscriptionEndDate: t.date().nullable().optional(),
    address2: t.string().nullable().optional(),
    emailAddress: t.string().nullable().optional(),
    websiteUrl: t.string().nullable().optional()
  }).optional().nullable(),
  funeralHomeSettings: t.object({
    datesOfLifeFormat: t.string().max(150).optional(),
    datesOfServiceFormat: t.string().max(105).optional(),
    birthHeader: t.string().max(500).optional(),
    deathHeader: t.string().max(500).optional(),
    remembranceHeader: t.string().max(500).optional(),
    language: t.nativeEnum(o),
    timePeriod: t.string().max(50).optional(),
    timeRangeSeparator: t.string().max(10).optional(),
    showTimeNoon: t.boolean().optional(),
    removeMinutes: t.boolean().optional()
  }).optional().nullable(),
  user: t.object({
    id: t.string().trim().optional(),
    name: t.string().trim(),
    email: t.string().email(),
    corporationId: t.string().nullable().optional()
  }).optional().nullable(),
  corporation: V
}), qn = t.object({
  products: t.union([
    t.string().array(),
    k.array()
  ]).optional(),
  themeId: t.string().trim().optional(),
  resetProductLayoutIds: t.union([t.array(t.string()), t.boolean()]).optional(),
  language: t.nativeEnum(o).optional(),
  funeralHome: t.object({
    id: t.string().trim().optional(),
    name: t.string().trim().optional(),
    phone: t.string().nullable().optional(),
    address: t.string().nullable().optional(),
    city: t.string().nullable().optional(),
    state: t.string().nullable().optional(),
    zipCode: t.string().nullable().optional(),
    corporationId: t.string().nullable().optional(),
    active: t.boolean().optional(),
    ipCity: t.string().nullable().optional(),
    ipCountryCode: t.string().nullable().optional(),
    ipCountryName: t.string().nullable().optional(),
    ipAddress: t.string().nullable().optional(),
    ipLatitude: t.number().nullable().optional(),
    ipLongitude: t.number().nullable().optional(),
    ipRegionCode: t.string().nullable().optional(),
    ipRegionName: t.string().nullable().optional(),
    ipZipCode: t.string().nullable().optional(),
    isServiceAuthenticated: t.boolean().optional(),
    freeTrialStartedAt: t.date().nullable().optional(),
    serviceUrlSlug: t.string().nullable().optional(),
    stripeCustomerId: t.string().nullable().optional(),
    stripeSubscriptionStatus: t.string().nullable().optional(),
    stripeSubscriptionEndDate: t.date().nullable().optional(),
    address2: t.string().nullable().optional(),
    emailAddress: t.string().nullable().optional(),
    websiteUrl: t.string().nullable().optional()
  }).optional()
}), Qn = t.object({
  productId: t.string().trim().uuid(),
  themeId: t.string().trim().uuid().optional(),
  // Per-line language override. Omit to inherit the order-level language.
  language: t.nativeEnum(o).optional(),
  // Per-line label shown as "Name (nickname)". Omit for no label.
  nickname: c,
  customizations: t.array(T).optional(),
  customizationsByLabel: j.optional()
}), $n = t.object({
  themeId: t.string().trim().uuid().optional(),
  // Per-line language override. An explicit `null` clears the override so
  // the line falls back to the order-level language; omit to leave as-is.
  language: t.nativeEnum(o).nullable().optional(),
  selected: t.boolean().optional(),
  // Per-line label shown as "Name (nickname)". `null` (or "") clears it;
  // omit to leave the stored value unchanged.
  nickname: c,
  customizations: t.array(T).optional(),
  customizationsByLabel: j.optional()
}).refine(
  (e) => e.themeId !== void 0 || e.language !== void 0 || e.selected !== void 0 || e.nickname !== void 0 || e.customizations !== void 0 || e.customizationsByLabel !== void 0,
  {
    message: "Provide at least one of: themeId, language, selected, nickname, customizations, customizationsByLabel"
  }
), Wn = t.object({
  actAsAdmin: t.boolean()
}), Kn = t.object({
  locationGuid: t.string().uuid("Invalid location GUID format")
}), Xn = t.object({
  caseId: t.string().regex(/^\d+$/, "Case ID must be a number").transform(Number)
}), Yn = t.object({
  funeralHomeId: t.string().regex(/^\d+$/, "Funeral Home ID must be a number").transform(Number)
}), Zn = t.object({
  pattern: t.string().min(1, "Cache pattern is required")
}), Jn = t.object({
  guid: t.string().uuid("Invalid location GUID format"),
  id: t.string().regex(/^\d+$/, "Case ID must be a number").transform(Number)
}), to = t.object({
  guid: t.string().uuid("Invalid location GUID format"),
  caseId: t.string().regex(/^\d+$/, "Case ID must be a number").transform(Number)
}), eo = t.object({
  guid: t.string().uuid("Invalid location GUID format"),
  id: t.string().regex(/^\d+$/, "Case ID must be a number").transform(Number)
}), no = t.object({
  guid: t.string().uuid("Invalid location GUID format")
}), oo = t.object({
  dateDied: t.string().min(1, "dateDied parameter is required"),
  lastName: t.string().min(1, "lastName parameter is required")
}), io = t.object({
  applicationType: a,
  id: t.string().uuid().optional(),
  name: t.string().trim().optional(),
  productId: t.string().uuid().optional(),
  m3ProductId: t.string().uuid().optional(),
  themeId: t.string().uuid().optional(),
  customPages: t.coerce.boolean().optional(),
  homeTemplate: t.coerce.boolean().optional(),
  default: t.coerce.boolean().optional(),
  orderId: t.string().uuid().optional(),
  language: t.nativeEnum(o).optional()
}), ao = t.object({
  applicationType: a,
  id: t.string().uuid().optional(),
  name: t.string().trim().optional(),
  productId: t.string().uuid().optional(),
  m3ProductId: t.string().uuid().optional(),
  themeId: t.string().uuid().optional(),
  customPages: t.coerce.boolean().optional(),
  homeTemplate: t.coerce.boolean().optional(),
  default: t.coerce.boolean().optional(),
  orderId: t.string().uuid().optional(),
  language: t.nativeEnum(o).optional()
}), ro = t.object({
  name: t.string().trim(),
  productId: t.string().uuid().optional(),
  m3ProductId: t.string().uuid().optional(),
  themeId: t.string().uuid(),
  homeTemplate: t.boolean(),
  default: t.coerce.boolean(),
  language: t.nativeEnum(o).default(o.EN),
  applicationType: a
}), lo = t.object({
  name: t.string().trim(),
  productId: t.string().uuid().optional(),
  m3ProductId: t.string().uuid().optional(),
  themeId: t.string().uuid(),
  homeTemplate: t.coerce.boolean(),
  default: t.coerce.boolean(),
  language: t.nativeEnum(o).default(o.EN)
}), so = t.object({
  pageSetId: t.string().uuid(),
  gridStartPointTop: t.number(),
  gridStartPointLeft: t.number(),
  rows: t.number(),
  columns: t.number(),
  pageNumber: t.number(),
  jsonTemplate: t.string(),
  sourceOrderPageId: t.string().uuid().optional()
}), q = t.object({
  gridStartPointTop: t.number(),
  gridStartPointLeft: t.number(),
  rows: t.number(),
  columns: t.number(),
  pageNumber: t.number(),
  jsonTemplate: t.string(),
  sourceOrderPageId: t.string().uuid()
}), uo = t.object({
  name: t.string().trim(),
  default: t.boolean(),
  language: t.nativeEnum(o),
  homeTemplate: t.boolean(),
  pages: t.array(q).min(1)
}), mo = t.object({
  name: t.string().trim(),
  productId: t.string().uuid().optional(),
  themeId: t.string().uuid(),
  default: t.boolean(),
  language: t.nativeEnum(o).default(o.EN),
  pages: t.array(q).min(1)
}), co = t.object({
  pageSetId: t.string().uuid().optional(),
  gridStartPointTop: t.number().optional(),
  gridStartPointLeft: t.number().optional(),
  rows: t.number().optional(),
  columns: t.number().optional(),
  pageNumber: t.number().optional(),
  template: t.string().optional(),
  jsonTemplate: t.string().optional(),
  archived: t.coerce.boolean().optional()
}), po = t.object({
  archived: t.coerce.boolean()
}), go = t.object({
  pageSetId: t.string().uuid()
}), bo = t.object({
  name: t.string().trim().optional(),
  productId: t.string().optional(),
  m3ProductId: t.string().optional(),
  themeId: t.string().optional(),
  funeralHomeId: t.string().optional(),
  homeTemplate: t.coerce.boolean().optional(),
  default: t.coerce.boolean().optional(),
  language: t.nativeEnum(o).optional()
}), Ct = 'may hold only letters, digits, "_" and "-"', Dt = t.object({
  itemId: t.string(),
  kind: t.enum(["tinymce", "lotus"]),
  pageIndex: t.number(),
  pageId: t.string().nullish(),
  settled: t.boolean(),
  frame: t.object({
    x: t.number(),
    y: t.number(),
    width: t.number(),
    height: t.number()
  }),
  fontSizesPt: t.array(t.number()).nullish(),
  scale: t.number().nullish(),
  lines: t.number().nullish(),
  loadHeight: t.number().nullish(),
  ceiling: t.number().nullish(),
  boundedBy: t.string().nullish(),
  overflowing: t.boolean().nullish(),
  /**
   * The room a flagged frame is past. The request loses its nulls on the way
   * (the API's denormalizer drops them), so a bound or a line height the
   * surface recorded as null arrives absent: both are read as nullish and
   * handed on as null, the shape the digest compares.
   */
  kept: t.object({
    ceiling: t.number(),
    boundedBy: t.string().nullish(),
    lineHeight: t.number().nullish()
  }).transform(({ ceiling: e, boundedBy: n, lineHeight: i }) => ({
    ceiling: e,
    boundedBy: n ?? null,
    lineHeight: i ?? null
  })).nullish(),
  sizing: t.string().nullish(),
  maxFontSizePt: t.number().nullish(),
  passes: t.number().nullish(),
  textHash: t.string().nullish(),
  stamps: t.record(t.union([t.string(), t.number(), t.boolean(), t.null()])).nullish()
}), At = t.object({
  surface: t.string(),
  takenAt: t.number().nullish(),
  boxes: t.array(Dt)
}), ho = t.object({
  html: t.string().trim(),
  pageHeight: t.coerce.number(),
  pageWidth: t.coerce.number(),
  orderId: t.string().trim(),
  productId: t.string().trim(),
  orderPageSetId: t.string().trim(),
  sendToWorker: t.boolean().optional(),
  frontendDomain: t.string().trim().optional(),
  /**
   * The server derives the job's funeral home from the order row and ignores
   * this field's value. The field is still required (an empty string passes),
   * and its pattern keeps a malformed value from passing the schema.
   */
  funeralHomeId: t.string().trim().regex(nt, Ct),
  applicationType: a,
  /**
   * The requesting surface's text geometry, so the worker can hold its own
   * paint against it box by box. Optional: a request without one is
   * rendered as before and compared against nothing.
   */
  textGeometry: At.optional()
}), Io = t.object({
  name: t.string().trim().optional()
}), fo = t.object({
  productId: t.string().uuid(),
  isM3: t.boolean().optional().default(!1)
}), vt = 25, _t = 25, So = 3, yo = 15, Lt = 2e3, Eo = t.object({
  orderId: t.string().min(1),
  // 'MDL' orders share through the MDL flow; 'M3' orders (opened in the MDL
  // editor via an edit token) are forwarded to the M3 internal proof-share API.
  applicationType: a,
  orderProductIds: t.array(t.string()).min(1).max(_t),
  recipientEmails: t.array(t.string().trim().email()).min(1).max(vt),
  // Reply-To on the share email. Optional: the server falls back to the
  // sharing user's email when omitted.
  replyToEmail: t.string().trim().email().optional(),
  // Optional free-text message from the director, rendered in the share email.
  comment: t.string().trim().max(Lt).optional()
}), p = t.enum([
  "DEFAULT",
  "VISITATION_SERVICE",
  "VISITATION_ALL",
  "SERVICES",
  "ORDER_OF_SERVICE",
  "PALLBEARERS"
]), To = p.options, jo = t.object({
  productId: t.string().uuid(),
  category: p.optional()
}), Oo = t.object({
  template: t.string().trim(),
  name: t.string().trim().nonempty(),
  productId: t.string().uuid(),
  themeId: t.string().uuid().optional().nullable(),
  category: p.optional()
}), Po = t.object({
  template: t.string().trim().optional(),
  name: t.string().trim().nonempty().optional(),
  category: p.optional()
}), Co = t.object({
  applicationType: a,
  layoutId: t.string().uuid().optional(),
  productId: t.string().uuid().optional(),
  themeId: t.string().uuid().optional(),
  archived: t.coerce.boolean().default(!1)
}), Do = t.object({
  //customThemes: z.coerce.boolean().optional().default(false),
  archived: t.coerce.boolean().optional().default(!1)
}), Ao = t.object({
  name: t.string().trim(),
  description: t.string().trim().optional(),
  image: t.string().trim().optional(),
  font: t.string().trim().transform(et).optional(),
  fontColorAccent1: t.string().trim().optional(),
  fontColorAccent2: t.string().trim().optional(),
  fontColor: t.string().trim().regex(/#\w+/, { message: "Invalid hex color" }).optional()
});
export {
  a as ApplicationTypeSchema,
  Nt as EDITOR_TEXT_BOX_ITEM_TYPE,
  Ct as FUNERAL_HOME_ID_SEGMENT_MESSAGE,
  ft as LOTUS_TURNOVER_TRANSACTION_BUDGET,
  Lt as MAX_PROOF_SHARE_COMMENT_LENGTH,
  _t as MAX_PROOF_SHARE_PRODUCTS,
  vt as MAX_PROOF_SHARE_RECIPIENTS,
  So as PROOF_SHARE_EXPIRATION_DAYS,
  yo as PROOF_SHARE_HOLD_TIMEOUT_MINUTES,
  zt as REDIS_CHANNELS,
  rt as RETURN_URL_MAX_LENGTH,
  To as SERVICE_TEMPLATE_CATEGORY_ORDER,
  s as UPSCALE_TARGET_EDGE_OUT_OF_RANGE_MESSAGE,
  ct as UPSCALE_TARGET_KIND_MESSAGE,
  un as addProductToBundleSchema,
  Fn as applyOrderPageSetLayoutSchema,
  po as archiveOrderPageSchema,
  Et as backgroundTypeSchema,
  ue as batchJobStatusInputSchema,
  Ne as bulkCreateCustomizationOptionsSchema,
  Zn as cachePatternSchema,
  ne as caseCreateAdditionalData,
  mt as caseCreateSchema,
  Xn as caseIdSchema,
  je as caseImageVariantAvailabilitySchema,
  dt as caseImageVariantForCaseListItemSchema,
  F as caseImageVariantListItemSchema,
  Te as caseImageVariantPricingSchema,
  Ie as caseImageVariantRefusedResponseSchema,
  Ee as caseImageVariantRevertedResponseSchema,
  Se as caseImageVariantsForCaseResponseSchema,
  fe as caseImageVariantsForImageResponseSchema,
  Oe as caseMemorialsSchema,
  te as casePreferencesSchema,
  y as casePreferencesUpdateSchema,
  ee as caseUpdateSchema,
  be as checkAllFamilyImagesInUseInputSchema,
  ge as checkFamilyImageInUseInputSchema,
  Ht as copyAllProductTemplatesSchema,
  V as corporationCreateSchema,
  Qt as createAssetSubcategoriesSchema,
  Wt as createAssetsSchema,
  he as createCaseImageVariantInputSchema,
  Pe as createCaseMemorialSchema,
  Ao as createCustomThemeSchema,
  Ce as createCustomVerseSchema,
  kt as createDefaultLayoutsForProductSchema,
  Oo as createGlobalServiceInformationTemplateSchema,
  Vn as createMetadataSchema,
  wt as createNewProductSchema,
  Nn as createOrderPageDetailsSchema,
  Mn as createOrderPageSetSchema,
  vn as createOrderProductsSchema,
  _n as createOrderSchema,
  so as createPageSchema,
  ro as createPageSetSchema,
  lo as createPageSetSchemaV2,
  Eo as createProofShareRequestSchema,
  kn as createSessionSchema,
  mo as createTemplateWithPagesSchema,
  Yt as createVerseFormatSchema,
  T as customizationByIdSelectionSchema,
  Qe as customizationCompareDetailsSchema,
  yt as customizationCompareTypeSchema,
  ve as customizationCreateSchema,
  z as customizationDisplayTypeSchema,
  Le as customizationFiltersSchema,
  Re as customizationIdSchema,
  m as customizationOptionCreateSchema,
  Ue as customizationOptionIdSchema,
  Ae as customizationOptionUpdateSchema,
  St as customizationQualitySchema,
  O as customizationTypeSchema,
  _e as customizationUpdateSchema,
  j as customizationsByLabelSchema,
  de as deleteAllFamilyImagesInputSchema,
  pe as deleteFamilyImageInputSchema,
  ae as deleteImageInputSchema,
  Gn as deleteOrderPagesBulkSchema,
  Me as designRequestQueueItemParamsSchema,
  Fe as designRequestQueueListQuerySchema,
  Hn as duplicateOrderSchema,
  Vt as duplicateThemeSchema,
  Q as editorBackButtonPayloadSchema,
  Mt as editorBackButtonRequestSchema,
  xt as editorBackButtonResponseSchema,
  xn as ensureOrderPageSetsSchema,
  me as familyPresignedUploadInputSchema,
  ce as familyProcessImageInputSchema,
  Yn as funeralHomeIdSchema,
  ho as generatePDFSchema,
  to as getAllExpressionGuestsSchema,
  oo as getAllFuneralHomeCasesQuerySchema,
  no as getAllFuneralHomeCasesSchema,
  qt as getAssetSubcategoriesSchema,
  ot as getAssetsSchema,
  eo as getCaseProfileImagesSchema,
  Jn as getCaseProfileSchema,
  Jt as getCasesSchema,
  In as getEditorOrderPageDetailsSchema,
  fn as getFuneralHomeOrdersSchema,
  jo as getGlobalServiceInformationTemplatesQuerySchema,
  Ut as getMeResponseSchema,
  Un as getOrderLayoutDetailsSchema,
  yn as getOrderPageDetailSchema,
  On as getOrderProductsSchema,
  jn as getOrderSchema,
  fo as getProductLayoutsSchema,
  Xt as getVerseFormatSchema,
  De as getVersesQuerySchema,
  ut as isAllowedReturnUrl,
  Gt as layoutTemplateCopySchema,
  Kn as locationGuidSchema,
  ze as lotusTurnoverBodySchema,
  It as lotusTurnoverPageSchema,
  Be as lotusTurnoverWorstCaseMs,
  He as m3CustomizationCreateSchema,
  ke as m3CustomizationFiltersSchema,
  Ve as m3CustomizationIdSchema,
  P as m3CustomizationOptionCreateSchema,
  qe as m3CustomizationOptionIdSchema,
  Ge as m3CustomizationOptionUpdateSchema,
  we as m3CustomizationUpdateSchema,
  Ye as m3ProductBulkCreateSchema,
  G as m3ProductCreateSchema,
  We as m3ProductFiltersSchema,
  Ke as m3ProductIdSchema,
  on as m3ProductImageBulkCreateSchema,
  H as m3ProductImageCreateSchema,
  nn as m3ProductImageFiltersSchema,
  en as m3ProductImageIdSchema,
  tn as m3ProductImageUpdateSchema,
  Xe as m3ProductItmRefSchema,
  $e as m3ProductUpdateSchema,
  jt as m3ProductVariationCreateSchema,
  Je as m3ProductVariationIdSchema,
  Tt as m3ProductVariationSelectionInputSchema,
  Ze as m3ProductVariationUpdateSchema,
  M as mdlCustomizationDisplayTypeSchema,
  E as mdlCustomizationTypeSchema,
  Ft as mdlEditorEventSchema,
  x as mdlThemeModeSchema,
  Bt as migrateDataSchema,
  re as moveToFrontInputSchema,
  U as nameArrangementSchema,
  Rn as orderLayoutDetailsSchema,
  Sn as orderPageDetailSchema,
  En as orderPageDetailSchemaV2,
  Dn as orderPageSetSchema,
  An as orderPageUpdateSchema,
  c as orderProductNicknameSchema,
  Pn as orderProductsSchemaV2,
  Cn as orderProductsUpdateSchema,
  Tn as orderSchema,
  io as pageSetSchema,
  ao as pageSetSchemaV2,
  go as pagesSchema,
  le as presignedUploadBatchInputSchema,
  oe as presignedUploadInputSchema,
  se as processBatchInputSchema,
  ie as processImageInputSchema,
  an as productBundleCreateSchema,
  ln as productBundleFiltersSchema,
  sn as productBundleIdSchema,
  w as productBundleItemSchema,
  rn as productBundleUpdateSchema,
  hn as productCategoryBulkCreateSchema,
  Ot as productCategoryCreateSchema,
  dn as productCategoryFiltersSchema,
  gn as productCategoryIdSchema,
  bn as productCategoryToggleAvailabilitySchema,
  pn as productCategoryUpdateSchema,
  k as productWithThemeAndCustomizationsSchema,
  Pt as productWithThemeSchema,
  Io as productsSchema,
  mn as removeProductFromBundleSchema,
  uo as replaceTemplatePagesSchema,
  Bn as resetOrderProductSchema,
  N as returnUrlSchema,
  p as serviceTemplateCategorySchema,
  ye as setCaseImageVariantRevertedInputSchema,
  Qn as singleOrderProductCreateSchema,
  $n as singleOrderProductUpdateSchema,
  q as templatePageBodySchema,
  Dt as textGeometryDigestBoxSchema,
  At as textGeometryDigestSchema,
  B as themeModeSchema,
  Co as themeOverridesSchema,
  Do as themesSchema,
  $t as updateAssetSubcategoriesSchema,
  Kt as updateAssetsSchema,
  cn as updateBundleItemQuantitySchema,
  Po as updateGlobalServiceInformationTemplateSchema,
  qn as updateMessengerOrderSchema,
  zn as updateOrderPageSetSchema,
  Ln as updateOrderSchema,
  co as updatePageSchema,
  bo as updatePageSetSchemaV2,
  Wn as updateUserActAsAdminSchema,
  Zt as updateVerseFormatSchema,
  pt as upscaleTargetSizeSchema,
  wn as validateOrderLanguageSchema,
  xe as watermarksSchema
};
